import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Heart, X, MessageSquare, Shield, User, Compass, ChevronRight,
  BadgeCheck, MapPin, Music, HandHelping, AlertTriangle, Flag,
  ShieldOff, VolumeX, Send, Loader2, CheckCircle2, Lock,
  Sparkles, Church, Globe, BookOpen, ChevronLeft, Info,
  HeartHandshake, Users, Star,
} from "lucide-react";
import { toast } from "sonner";
import VerifiedBadge from "../components/VerifiedBadge";

// ── Constants ─────────────────────────────────────────────────────────────────
const INTENT_LABELS = {
  friendship: "Friendship",
  relationship: "Serious Relationship",
  marriage: "Marriage-Minded",
  paused: "Paused",
};

const INTENT_ICONS = {
  friendship: Users,
  relationship: Heart,
  marriage: HeartHandshake,
  paused: VolumeX,
};

const STATUS_LABELS = {
  new: "New Match",
  active: "Active",
  quiet: "Quiet — 7 days",
  reminder_sent: "Reminder Sent",
  paused: "Paused",
  expired: "Expired",
  ended: "Ended",
  under_review: "Under Review",
};

const STATUS_COLORS = {
  new: "bg-emerald-100 text-emerald-700 border-emerald-200",
  active: "bg-blue-100 text-blue-700 border-blue-200",
  quiet: "bg-amber-100 text-amber-700 border-amber-200",
  under_review: "bg-red-100 text-red-700 border-red-200",
};

const REL_STATUS_LABELS = {
  friends: "Friends",
  courtship: "In Courtship",
  relationship: "In Relationship",
  engaged: "Engaged",
  married: "Married",
};

const REPORT_REASONS = [
  "Requesting money or financial scam",
  "Fake or suspicious profile",
  "Inappropriate or explicit content",
  "Harassment or threatening behaviour",
  "Trying to move off-platform too quickly",
  "Love bombing or manipulation",
  "Other",
];

const ICEBREAKERS = [
  "What is your favourite CCC hymn and why?",
  "How long have you been a member of CCC?",
  "What ministry or service do you enjoy most in church?",
  "Do you play any musical instruments?",
  "What does your daily prayer life look like?",
  "Is there a hymn you would love to learn this year?",
  "What role do you serve in your parish?",
];

// ── Small helpers ─────────────────────────────────────────────────────────────

function ConfirmModal({ title, message, confirmLabel = "Confirm", danger = false, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center px-4" onClick={onCancel}>
      <div className="card-surface max-w-sm w-full p-6 space-y-4 rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="font-display text-xl text-[var(--brand-primary)]">{title}</div>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{message}</p>
        <div className="flex gap-3 pt-1">
          <button onClick={onCancel} className="flex-1 min-h-[48px] text-sm font-medium border border-[var(--border-default)] rounded-xl text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] transition-colors active:scale-95">Cancel</button>
          <button onClick={onConfirm} className={`flex-1 min-h-[48px] text-sm font-semibold rounded-xl transition-colors active:scale-95 ${danger ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white"}`}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function Avatar({ member, size = 12 }) {
  const px = size * 4;
  const style = { width: px, height: px, minWidth: px, minHeight: px };
  const base = "rounded-full overflow-hidden flex items-center justify-center font-bold text-white shrink-0";
  if (member?.avatar) {
    return <div className={base} style={style}><img src={member.avatar} alt={member.name} className="w-full h-full object-cover" /></div>;
  }
  const fontSize = px >= 48 ? "1.25rem" : px >= 40 ? "1rem" : "0.8125rem";
  return (
    <div className={`${base} bg-[var(--brand-primary)]`} style={{ ...style, fontSize }}>
      {(member?.name || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

function IntentBadge({ intent }) {
  const map = {
    friendship: "bg-blue-50 text-blue-700 border-blue-200",
    relationship: "bg-rose-50 text-rose-700 border-rose-200",
    marriage: "bg-purple-50 text-purple-700 border-purple-200",
    paused: "bg-gray-100 text-gray-500 border-gray-200",
  };
  const Icon = INTENT_ICONS[intent] || Users;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border font-semibold ${map[intent] || map.friendship}`}>
      <Icon size={11} /> {INTENT_LABELS[intent] || intent}
    </span>
  );
}

function VerificationBadge({ level }) {
  if (!level || level === "none") return null;
  const map = {
    profile: { cls: "bg-blue-100 text-blue-700", label: "Profile Verified" },
    parish: { cls: "bg-emerald-100 text-emerald-700", label: "Parish Verified" },
    identity: { cls: "bg-purple-100 text-purple-700", label: "Identity Verified" },
    trusted: { cls: "bg-amber-100 text-amber-700", label: "Trusted Member" },
  };
  const { cls, label } = map[level] || map.profile;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full font-semibold ${cls}`}>
      <BadgeCheck size={11} /> {label}
    </span>
  );
}

function SafetyBanner({ text }) {
  return (
    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800">
      <AlertTriangle size={18} className="shrink-0 mt-0.5 text-amber-600" />
      <p className="text-sm leading-relaxed">{text}</p>
    </div>
  );
}

// ── Profile-completion gate ───────────────────────────────────────────────────
function EligibilityGate({ missing }) {
  const navigate = useNavigate();
  const fieldIcons = {
    photo: User,
    location: MapPin,
    summary: BookOpen,
    rank: Star,
    verified: BadgeCheck,
    parish: Church,
  };
  return (
    <div className="max-w-lg mx-auto py-10 px-4 space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 rounded-2xl bg-[var(--brand-primary)] grid place-items-center mx-auto">
          <HeartHandshake size={28} className="text-[var(--brand-accent)]" />
        </div>
        <h2 className="font-display text-2xl text-[var(--brand-primary)]">Celestial Match</h2>
        <p className="text-sm text-[var(--text-secondary)] max-w-sm mx-auto">
          Before you can meet fellow Celestials here, please complete the items below. This helps ensure a safe, trusted experience for everyone.
        </p>
      </div>

      <div className="space-y-3">
        {missing.map((item) => {
          const Icon = fieldIcons[item.field] || BadgeCheck;
          return (
            <div key={item.field} className="card-surface p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[var(--brand-accent)]/10 grid place-items-center shrink-0">
                <Icon size={18} className="text-[var(--brand-accent)]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-[var(--brand-primary)]">{item.label}</div>
                <div className="text-xs text-[var(--text-secondary)]">{item.action}</div>
              </div>
              <button
                onClick={() => navigate(item.route)}
                className="text-sm px-4 min-h-[44px] rounded-xl bg-[var(--brand-primary)] text-white font-semibold active:scale-95 transition-transform"
              >
                Go →
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-[var(--text-tertiary)]">
        These requirements are set by your church admin to ensure the safety and integrity of this space.
      </p>
    </div>
  );
}

// ── Connect Profile Setup ─────────────────────────────────────────────────────
function ConnectProfileSetup({ onSaved }) {
  const [form, setForm] = useState({
    intent: "friendship",
    visible: true,
    favorite_hymn: "",
    choir_experience: "",
    instruments: [],
    service_preference: "",
    spiritual_habits: "",
    learn_hymns_together: false,
    study_constitution_together: false,
    cpmwave_interest: false,
    icebreaker_1: "",
    icebreaker_2: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await http.put("/connect/me", form);
      toast.success("Connect profile saved! Alleluia!");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const intents = [
    { value: "friendship", label: "Friendship", desc: "Connect as trusted community friends", Icon: Users },
    { value: "relationship", label: "Serious Relationship", desc: "Open to a meaningful, intentional relationship", Icon: Heart },
    { value: "marriage", label: "Marriage-Minded", desc: "Seeking courtship leading to marriage", Icon: HeartHandshake },
    { value: "paused", label: "Not Currently Open", desc: "Pause your profile temporarily", Icon: VolumeX },
  ];

  return (
    <div className="max-w-xl mx-auto py-8 px-4 space-y-6">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.22em] text-[var(--brand-accent)] mb-1">Celestial Match</div>
        <h2 className="font-display text-2xl text-[var(--brand-primary)]">Set Up Your Connect Profile</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Tell us a little about your intention so we can help you find the right connections.</p>
      </div>

      <div className="card-surface p-5 space-y-4">
        <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">My Intention</div>
        <div className="grid grid-cols-1 gap-2">
          {intents.map(({ value, label, desc, Icon }) => (
            <button
              key={value}
              onClick={() => setForm((f) => ({ ...f, intent: value }))}
              className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${form.intent === value ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : "border-[var(--border-default)] hover:border-[var(--brand-primary)]/40"}`}
            >
              <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${form.intent === value ? "bg-[var(--brand-primary)] text-white" : "bg-[var(--bg-subtle)] text-[var(--text-secondary)]"}`}>
                <Icon size={16} />
              </div>
              <div>
                <div className={`text-sm font-medium ${form.intent === value ? "text-[var(--brand-primary)]" : "text-[var(--text-primary)]"}`}>{label}</div>
                <div className="text-xs text-[var(--text-tertiary)]">{desc}</div>
              </div>
              {form.intent === value && <CheckCircle2 size={16} className="ml-auto text-[var(--brand-primary)]" />}
            </button>
          ))}
        </div>
      </div>

      <div className="card-surface p-5 space-y-4">
        <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">Faith & Worship</div>
        <label className="text-sm block">
          Favourite CCC Hymn
          <input className="input-clean mt-1 w-full" value={form.favorite_hymn} onChange={(e) => setForm((f) => ({ ...f, favorite_hymn: e.target.value }))} placeholder="e.g. Hymn 1 — Holy Holy Holy…" />
        </label>
        <label className="text-sm block">
          Choir / Music Experience
          <input className="input-clean mt-1 w-full" value={form.choir_experience} onChange={(e) => setForm((f) => ({ ...f, choir_experience: e.target.value }))} placeholder="e.g. Soprano in choir, 5 years" />
        </label>
        <label className="text-sm block">
          How you prefer to serve in church
          <input className="input-clean mt-1 w-full" value={form.service_preference} onChange={(e) => setForm((f) => ({ ...f, service_preference: e.target.value }))} placeholder="e.g. Intercessory prayer, Ushering…" />
        </label>
        <label className="text-sm block">
          Spiritual growth habits
          <input className="input-clean mt-1 w-full" value={form.spiritual_habits} onChange={(e) => setForm((f) => ({ ...f, spiritual_habits: e.target.value }))} placeholder="e.g. Daily morning prayer, Bible study…" />
        </label>
        <div className="space-y-2">
          {[
            ["learn_hymns_together", "Open to learning CCC hymns together"],
            ["study_constitution_together", "Open to studying the CCC Constitution together"],
            ["cpmwave_interest", "Interested in sharing worship music via CPM Wave"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))} className="accent-[var(--brand-primary)]" />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="card-surface p-5 space-y-4">
        <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">Faith Conversation Starters</div>
        <p className="text-xs text-[var(--text-secondary)]">Choose prompts that will appear on your card to start meaningful conversations.</p>
        {[["icebreaker_1", "First prompt"], ["icebreaker_2", "Second prompt"]].map(([key, label]) => (
          <label key={key} className="text-sm block">
            {label}
            <select className="input-clean mt-1 w-full" value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}>
              <option value="">— Choose a prompt —</option>
              {ICEBREAKERS.map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
          </label>
        ))}
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer card-surface px-4 py-3">
        <input type="checkbox" checked={form.visible} onChange={(e) => setForm((f) => ({ ...f, visible: e.target.checked }))} className="accent-[var(--brand-primary)]" />
        Show my profile in Discover
      </label>

      <SafetyBanner text="Celestial Match is a faith-centred space for finding genuine friendship and serious relationships within the Celestial community. Please be honest, respectful, and God-honouring in all interactions." />

      <button onClick={save} disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 py-3 text-base font-semibold">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
        Save Connect Profile
      </button>
    </div>
  );
}

// ── Discover Tab ──────────────────────────────────────────────────────────────
function DiscoverTab({ settings }) {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [swipeBusy, setSwipeBusy] = useState(false);
  const [intentFilter, setIntentFilter] = useState("");
  const [matchFlash, setMatchFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setIdx(0);
    try {
      const params = {};
      if (intentFilter) params.intent = intentFilter;
      const { data } = await http.get("/connect/discover", { params });
      setCards(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }, [intentFilter]);

  useEffect(() => { load(); }, [load]);

  const swipe = async (action) => {
    const card = cards[idx];
    if (!card || swipeBusy) return;
    setSwipeBusy(true);
    try {
      const { data } = await http.post("/connect/swipe", { target_id: card.id, action });
      if (data.match_created) {
        setMatchFlash(card);
      } else if (action === "like") {
        toast.success("Interest sent! Waiting for them to respond.");
      }
      setIdx((i) => i + 1);
    } catch (e) { toast.error(formatErr(e)); } finally { setSwipeBusy(false); }
  };

  const card = cards[idx];
  const cp = card?.connect_profile || {};

  return (
    <div className="space-y-4">
      <SafetyBanner text={settings?.safety_banner_text || "Keep conversations respectful. Do not request or send money. Report suspicious behaviour immediately."} />

      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-sm text-[var(--text-tertiary)] font-medium">Show:</span>
        {[["", "Everyone"], ["friendship", "Friendship"], ["relationship", "Relationship"], ["marriage", "Marriage-Minded"]].map(([v, l]) => (
          <button key={v} onClick={() => setIntentFilter(v)} className={`text-sm px-4 py-2 min-h-[40px] rounded-full border font-medium transition-all active:scale-95 ${intentFilter === v ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--brand-primary)]/40"}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-20 gap-3">
          <Loader2 size={32} className="animate-spin text-[var(--brand-accent)]" />
          <p className="text-sm text-[var(--text-secondary)]">Finding connections…</p>
        </div>
      ) : !card ? (
        <div className="card-surface p-10 text-center space-y-3">
          <HeartHandshake size={40} className="mx-auto text-[var(--text-tertiary)]" />
          <p className="font-display text-lg text-[var(--brand-primary)]">You&apos;ve seen everyone for now</p>
          <p className="text-sm text-[var(--text-secondary)]">Check back later as new members join Celestial Match. May God guide your path.</p>
          <button onClick={load} className="text-sm px-4 py-2 rounded-lg border border-[var(--border-default)] hover:bg-[var(--bg-subtle)]">Refresh</button>
        </div>
      ) : (
        <div className="relative">
          {/* Background preview card */}
          {cards[idx + 1] && (
            <div className="absolute inset-x-4 top-2 bottom-0 rounded-2xl card-surface opacity-50 scale-95 pointer-events-none" />
          )}

          {/* Main card */}
          <div className="card-surface rounded-2xl overflow-hidden relative z-10">
            <div className="relative h-64 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-accent)]">
              {card.avatar ? (
                <img src={card.avatar} alt={card.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="font-display text-7xl text-white/80">{(card.name || "?")[0]}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--brand-primary)]/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-end gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-2xl text-white">{card.name}</span>
                      {card.verified && <VerifiedBadge size="sm" />}
                      {cp.verification_status && cp.verification_status !== "none" && <VerificationBadge level={cp.verification_status} />}
                    </div>
                    {card.ccc_rank && <div className="text-sm text-white/80">{card.ccc_rank}</div>}
                    {(card.city || card.country) && (
                      <div className="text-xs text-white/70 flex items-center gap-1 mt-0.5">
                        <MapPin size={10} /> {[card.city, card.country].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </div>
                  <IntentBadge intent={cp.intent} />
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {card.parish && (
                <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)] bg-[var(--bg-subtle)] rounded-lg px-3 py-2">
                  <Church size={12} className="text-[var(--brand-accent)]" /> {card.parish.name} · {card.parish.city}
                </div>
              )}

              {card.profile_summary && (
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed line-clamp-3">{card.profile_summary}</p>
              )}

              <div className="flex flex-wrap gap-2">
                {cp.choir_experience && <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1.5 font-medium"><Music size={11} /> {cp.choir_experience}</span>}
                {cp.service_preference && <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1.5 font-medium"><HandHelping size={11} /> {cp.service_preference}</span>}
                {cp.learn_hymns_together && <span className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1.5 font-medium"><Music size={11} /> Open to learning hymns</span>}
                {cp.study_constitution_together && <span className="text-xs px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1.5 font-medium"><BookOpen size={11} /> Constitution study</span>}
                {cp.cpmwave_interest && <span className="text-xs px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-1.5 font-medium"><Music size={11} /> CPM Wave music</span>}
              </div>

              {(cp.icebreaker_1 || cp.icebreaker_2) && (
                <div className="space-y-2 border-t border-[var(--border-default)] pt-3">
                  {[cp.icebreaker_1, cp.icebreaker_2].filter(Boolean).map((q, i) => (
                    <div key={i} className="text-xs bg-[var(--brand-accent)]/8 rounded-lg px-3 py-2 text-[var(--brand-primary)] italic">
                      &ldquo;{q}&rdquo;
                    </div>
                  ))}
                </div>
              )}

              {cp.favorite_hymn && (
                <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                  <Music size={11} className="text-[var(--brand-accent)]" />
                  <span>Favourite hymn: <span className="font-medium text-[var(--brand-primary)]">{cp.favorite_hymn}</span></span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => swipe("pass")}
                  disabled={swipeBusy}
                  className="flex-1 flex items-center justify-center gap-2 min-h-[52px] rounded-2xl border-2 border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50 transition-all active:scale-95 font-semibold text-base"
                >
                  {swipeBusy ? <Loader2 size={20} className="animate-spin" /> : <X size={22} />}
                  Pass
                </button>
                <button
                  onClick={() => swipe("like")}
                  disabled={swipeBusy}
                  className="flex-1 flex items-center justify-center gap-2 min-h-[52px] rounded-2xl bg-[var(--brand-primary)] text-white hover:opacity-90 transition-all active:scale-95 font-semibold text-base"
                >
                  {swipeBusy ? <Loader2 size={20} className="animate-spin" /> : <Heart size={22} />}
                  Connect
                </button>
              </div>

              <div className="text-center text-xs text-[var(--text-tertiary)]">
                {idx + 1} of {cards.length} profiles · <button onClick={() => setIdx((i) => Math.max(0, i - 1))} className="hover:underline">Back</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {matchFlash && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center px-4" onClick={() => setMatchFlash(null)}>
          <div className="card-surface max-w-sm w-full p-8 text-center rounded-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-5xl mb-3">🕊️</div>
            <div className="font-display text-2xl text-[var(--brand-primary)] mb-1">Alleluia!</div>
            <div className="text-lg font-semibold text-[var(--brand-primary)] mb-2">You matched with {matchFlash.name}!</div>
            <p className="text-sm text-[var(--text-secondary)] mb-5">You can now begin a conversation. Keep it respectful, prayerful, and purposeful.</p>
            <div className="flex gap-2">
              <button onClick={() => setMatchFlash(null)} className="flex-1 py-2.5 rounded-xl border border-[var(--border-default)] text-sm">Continue Discovering</button>
              <button onClick={() => { setMatchFlash(null); /* handled by tab switch */ }} className="flex-1 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-medium">Go to Matches</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Matches Tab ───────────────────────────────────────────────────────────────
function MatchesTab({ onOpenChat }) {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/connect/matches");
      setMatches(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const endMatch = (matchId, name) => setConfirm({ matchId, name });

  const doEndMatch = async () => {
    if (!confirm) return;
    try {
      await http.post(`/connect/matches/${confirm.matchId}/end`);
      toast.success("Connection ended");
      setConfirm(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-[var(--brand-accent)]" /></div>;

  if (matches.length === 0) return (
    <div className="card-surface p-10 text-center space-y-2">
      <HeartHandshake size={40} className="mx-auto text-[var(--text-tertiary)]" />
      <p className="font-display text-lg text-[var(--brand-primary)]">No connections yet</p>
      <p className="text-sm text-[var(--text-secondary)]">When you and another member both express interest, you will appear here. Start discovering in the Discover tab.</p>
    </div>
  );

  return (
    <>
      {confirm && (
        <ConfirmModal
          title="End Connection"
          message={`End your connection with ${confirm.name}? This cannot be undone.`}
          confirmLabel="End Connection"
          danger
          onConfirm={doEndMatch}
          onCancel={() => setConfirm(null)}
        />
      )}
      <div className="space-y-3">
        <div className="text-sm text-[var(--text-secondary)] font-medium">{matches.length} active connection{matches.length !== 1 ? "s" : ""}</div>
        {matches.map((m) => {
          const other = m.other_user || {};
          const ocp = m.other_connect_profile || {};
          const statusCls = STATUS_COLORS[m.status] || "bg-gray-100 text-gray-600 border-gray-200";
          return (
            <div key={m.id} className="card-surface p-4 flex items-center gap-3">
              <button onClick={() => onOpenChat(m)} className="flex-1 flex items-center gap-3 text-left min-h-[56px]">
                <div className="relative shrink-0">
                  <Avatar member={other} size={12} />
                  {m.unread_count > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-xs font-bold grid place-items-center px-0.5">
                      {m.unread_count}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[var(--brand-primary)] truncate">{other.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${statusCls}`}>{STATUS_LABELS[m.status] || m.status}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <IntentBadge intent={ocp.intent} />
                    {m.relationship_status && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-semibold">
                        {REL_STATUS_LABELS[m.relationship_status]}
                      </span>
                    )}
                  </div>
                  {m.last_message && (
                    <p className="text-xs text-[var(--text-tertiary)] mt-1 truncate">
                      {m.last_message.sender_id === user?.id ? "You: " : ""}{m.last_message.body}
                    </p>
                  )}
                  <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
                    Matched {new Date(m.matched_at).toLocaleDateString()}
                  </p>
                </div>
                <ChevronRight size={16} className="text-[var(--text-tertiary)] shrink-0" />
              </button>
              <button onClick={() => endMatch(m.id, other.name)} title="End connection" className="p-3 min-w-[44px] min-h-[44px] rounded-xl text-[var(--text-tertiary)] hover:text-red-600 hover:bg-red-50 transition-colors grid place-items-center">
                <X size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Chat View ─────────────────────────────────────────────────────────────────
function ChatView({ match, onBack }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSafety, setShowSafety] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportNote, setReportNote] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [showTestimony, setShowTestimony] = useState(false);
  const [testimonyForm, setTestimonyForm] = useState({ title: "", story: "" });
  const [testimonyBusy, setTestimonyBusy] = useState(false);
  const [blockConfirm, setBlockConfirm] = useState(false);
  const bottomRef = useRef(null);
  const other = match.other_user || {};
  const ocp = match.other_connect_profile || {};
  const settings = match._settings || {};

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/connect/matches/${match.id}/messages`);
      setMessages(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }, [match.id]);

  useEffect(() => { loadMessages(); }, [loadMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await http.post(`/connect/matches/${match.id}/messages`, { body: text });
      if (data.warned && data.warn_message) {
        toast.warning(data.warn_message);
      }
      setText("");
      setMessages((m) => [...m, data]);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const submitReport = async () => {
    if (!reportReason) return;
    setReportBusy(true);
    try {
      await http.post("/connect/report", { target_user_id: other.id, match_id: match.id, reason: reportReason, note: reportNote });
      toast.success("Report submitted. Our moderation team will review it.");
      setShowReport(false);
    } catch (e) { toast.error(formatErr(e)); } finally { setReportBusy(false); }
  };

  const blockUser = async () => {
    try {
      await http.post(`/connect/block/${other.id}`);
      toast.success(`${other.name} has been blocked`);
      setBlockConfirm(false);
      onBack();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const mute = async () => {
    try {
      const { data } = await http.post(`/connect/matches/${match.id}/mute`);
      toast.success(data.muted ? "Conversation muted" : "Conversation unmuted");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submitTestimony = async () => {
    if (!testimonyForm.title || !testimonyForm.story) { toast.error("Please fill in both fields"); return; }
    setTestimonyBusy(true);
    try {
      await http.post(`/connect/matches/${match.id}/testimony`, testimonyForm);
      toast.success("Testimony submitted for admin review. Alleluia!");
      setShowTestimony(false);
    } catch (e) { toast.error(formatErr(e)); } finally { setTestimonyBusy(false); }
  };

  const safetyTips = settings?.chat_safety_tips || [
    "Never share personal financial information.",
    "Report any request for money immediately.",
    "Meet only in public, church settings at first.",
    "Trust your instincts — if something feels wrong, report it.",
    "Keep God first in all your communications.",
  ];

  return (
    <div className="flex flex-col h-[calc(100dvh-160px)] min-h-[400px] relative">
      {blockConfirm && (
        <ConfirmModal
          title={`Block ${other.name}`}
          message="Blocking this person will also end your connection. They will not be notified who blocked them."
          confirmLabel="Block"
          danger
          onConfirm={blockUser}
          onCancel={() => setBlockConfirm(false)}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-2 pb-3 border-b border-[var(--border-default)] shrink-0">
        <button onClick={onBack} className="p-2.5 min-w-[44px] min-h-[44px] rounded-xl hover:bg-[var(--bg-subtle)] grid place-items-center"><ChevronLeft size={20} /></button>
        <Avatar member={other} size={10} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[var(--brand-primary)] truncate">{other.name}</span>
            {ocp.verification_status && ocp.verification_status !== "none" && <VerificationBadge level={ocp.verification_status} />}
          </div>
          <IntentBadge intent={ocp.intent} />
        </div>
        <div className="flex gap-0.5 shrink-0">
          <button onClick={() => setShowSafety((s) => !s)} title="Safety tips" className="p-2.5 min-w-[44px] min-h-[44px] rounded-xl hover:bg-amber-50 text-amber-600 grid place-items-center"><Shield size={18} /></button>
          <button onClick={mute} title="Mute" className="p-2.5 min-w-[44px] min-h-[44px] rounded-xl hover:bg-[var(--bg-subtle)] text-[var(--text-secondary)] grid place-items-center"><VolumeX size={18} /></button>
          <button onClick={() => setShowReport(true)} title="Report" className="p-2.5 min-w-[44px] min-h-[44px] rounded-xl hover:bg-red-50 text-red-500 grid place-items-center"><Flag size={18} /></button>
          <button onClick={() => setBlockConfirm(true)} title="Block" className="p-2.5 min-w-[44px] min-h-[44px] rounded-xl hover:bg-red-50 text-red-600 grid place-items-center"><ShieldOff size={18} /></button>
        </div>
      </div>

      {/* Safety tips panel */}
      {showSafety && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-2 shrink-0">
          <div className="flex items-center gap-2 mb-2 text-amber-800 font-semibold text-sm">
            <Shield size={14} /> Safety Tips
          </div>
          <ul className="space-y-1">
            {safetyTips.map((tip, i) => <li key={i} className="text-xs text-amber-700 flex items-start gap-1.5"><span className="text-amber-500 mt-0.5">✓</span>{tip}</li>)}
          </ul>
          <div className="mt-3 flex gap-2">
            <button onClick={() => setShowTestimony(true)} className="text-xs px-3 py-1.5 rounded-lg bg-purple-100 text-purple-700 border border-purple-200 font-medium">Share Testimony</button>
            <button onClick={() => setShowStatusUpdate(true)} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-200 font-medium">Update Relationship Status</button>
          </div>
        </div>
      )}

      {/* Match first-time safety notice */}
      {messages.length === 0 && !loading && (
        <div className="bg-[var(--brand-primary)]/5 border border-[var(--brand-primary)]/20 rounded-xl p-4 mt-2 shrink-0">
          <div className="flex items-center gap-2 mb-2 text-[var(--brand-primary)] font-semibold text-sm"><Sparkles size={14} /> Alleluia! You&apos;re connected!</div>
          <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
            Begin your conversation thoughtfully. Keep all interactions respectful, appropriate, and prayerful. Do not share financial details or ask for money.
          </p>
          {ocp.icebreaker_1 && (
            <button onClick={() => setText(ocp.icebreaker_1)} className="mt-3 text-sm px-4 py-3 min-h-[48px] rounded-xl bg-[var(--brand-primary)] text-white w-full text-left truncate font-medium">
              💬 &ldquo;{ocp.icebreaker_1}&rdquo;
            </button>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3">
        {loading && <div className="flex justify-center"><Loader2 size={20} className="animate-spin text-[var(--brand-accent)]" /></div>}
        {messages.map((msg) => {
          const mine = msg.sender_id === user?.id;
          return (
            <div key={msg.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[var(--brand-primary)] text-white rounded-br-md" : "bg-[var(--bg-subtle)] text-[var(--text-primary)] rounded-bl-md"}`}>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.body}</p>
                <p className={`text-xs mt-1 opacity-70`}>
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  {msg.moderation_status === "warn" && <span className="ml-1 text-amber-400">⚠</span>}
                  {msg.moderation_status === "flag" && <span className="ml-1 text-red-400">🚩</span>}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      {["expired", "ended", "under_review"].includes(match.status) ? (
        <div className="text-center text-xs text-[var(--text-tertiary)] py-3 border-t border-[var(--border-default)] shrink-0">
          This connection is {match.status}. Messaging is disabled.
        </div>
      ) : (
        <div className="flex gap-2 pt-3 border-t border-[var(--border-default)] shrink-0">
          <input
            className="input-clean flex-1"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
            placeholder="Type a message…"
          />
          <button onClick={send} disabled={busy || !text.trim()} className="btn-primary px-4 inline-flex items-center gap-1">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      )}

      {/* Report modal */}
      {showReport && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center px-4" onClick={() => setShowReport(false)}>
          <div className="card-surface max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-lg text-[var(--brand-primary)]">Report {other.name}</div>
            <div className="space-y-2">
              {REPORT_REASONS.map((r) => (
                <button key={r} onClick={() => setReportReason(r)} className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-all ${reportReason === r ? "border-red-400 bg-red-50 text-red-700" : "border-[var(--border-default)] hover:bg-[var(--bg-subtle)]"}`}>
                  {r}
                </button>
              ))}
            </div>
            <textarea className="input-clean w-full min-h-[70px] resize-none text-sm" value={reportNote} onChange={(e) => setReportNote(e.target.value)} placeholder="Add any additional details (optional)…" />
            <div className="flex gap-2">
              <button onClick={() => setShowReport(false)} className="flex-1 min-h-[48px] text-sm font-medium border border-[var(--border-default)] rounded-xl text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] transition-colors">Cancel</button>
              <button onClick={submitReport} disabled={!reportReason || reportBusy} className="flex-1 min-h-[48px] text-sm bg-red-600 text-white rounded-xl font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-50">
                {reportBusy && <Loader2 size={14} className="animate-spin" />} Submit Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status update modal */}
      {showStatusUpdate && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center px-4" onClick={() => setShowStatusUpdate(false)}>
          <div className="card-surface max-w-sm w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-lg text-[var(--brand-primary)]">Update Relationship Status</div>
            <p className="text-xs text-[var(--text-secondary)]">Both of you must agree on the status. Your partner will be asked to confirm.</p>
            <div className="space-y-2">
              {Object.entries(REL_STATUS_LABELS).map(([v, l]) => (
                <button key={v} onClick={async () => {
                  try {
                    const { data } = await http.post(`/connect/matches/${match.id}/status`, { relationship_status: v });
                    toast.success(data.agreed ? `Status updated to ${l}!` : `Request sent. Waiting for ${other.name} to confirm.`);
                    setShowStatusUpdate(false);
                  } catch (e) { toast.error(formatErr(e)); }
                }} className="w-full text-left text-sm px-4 py-3 min-h-[48px] rounded-xl border border-[var(--border-default)] hover:bg-[var(--bg-subtle)] hover:border-[var(--brand-primary)] font-medium transition-colors">
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Testimony modal */}
      {showTestimony && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center px-4" onClick={() => setShowTestimony(false)}>
          <div className="card-surface max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-lg text-[var(--brand-primary)]">Share Your Testimony</div>
            <p className="text-xs text-[var(--text-secondary)]">If God has blessed your connection, share your journey. Admin will review before it is published.</p>
            <input className="input-clean w-full" value={testimonyForm.title} onChange={(e) => setTestimonyForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title (e.g. God brought us together)" />
            <textarea className="input-clean w-full min-h-[120px] resize-none text-sm" value={testimonyForm.story} onChange={(e) => setTestimonyForm((f) => ({ ...f, story: e.target.value }))} placeholder="Tell your story…" />
            <div className="flex gap-2">
              <button onClick={() => setShowTestimony(false)} className="flex-1 min-h-[48px] text-sm font-medium border border-[var(--border-default)] rounded-xl text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] transition-colors">Cancel</button>
              <button onClick={submitTestimony} disabled={testimonyBusy} className="flex-1 btn-primary min-h-[48px] text-sm inline-flex items-center justify-center gap-1">
                {testimonyBusy && <Loader2 size={14} className="animate-spin" />} Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Chats Tab ─────────────────────────────────────────────────────────────────
function ChatsTab({ initialMatch = null }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeMatch, setActiveMatch] = useState(initialMatch);
  const [settings, setSettings] = useState({});
  const { user } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [matchRes, settingsRes] = await Promise.all([
        http.get("/connect/matches"),
        http.get("/connect/settings"),
      ]);
      setMatches(matchRes.data);
      setSettings(settingsRes.data);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (initialMatch) setActiveMatch(initialMatch);
  }, [initialMatch]);

  if (activeMatch) {
    return <ChatView match={{ ...activeMatch, _settings: settings }} onBack={() => { setActiveMatch(null); load(); }} />;
  }

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-[var(--brand-accent)]" /></div>;

  const withMessages = matches.filter((m) => m.last_message);
  const withoutMessages = matches.filter((m) => !m.last_message);

  return (
    <div className="space-y-4">
      {matches.length === 0 ? (
        <div className="card-surface p-10 text-center space-y-2">
          <MessageSquare size={40} className="mx-auto text-[var(--text-tertiary)]" />
          <p className="font-display text-lg text-[var(--brand-primary)]">No conversations yet</p>
          <p className="text-sm text-[var(--text-secondary)]">Conversations open after you and another member match. Start discovering!</p>
        </div>
      ) : (
        <>
          {withMessages.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">Recent</div>
              {withMessages.map((m) => {
                const other = m.other_user || {};
                return (
                  <button key={m.id} onClick={() => setActiveMatch(m)} className="w-full card-surface p-3 flex items-center gap-3 text-left hover:shadow-md transition-shadow">
                    <div className="relative shrink-0">
                      <Avatar member={other} size={11} />
                      {m.unread_count > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[9px] font-bold grid place-items-center px-0.5">{m.unread_count}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium truncate ${m.unread_count > 0 ? "text-[var(--brand-primary)]" : "text-[var(--text-primary)]"}`}>{other.name}</span>
                        <span className="text-[10px] text-[var(--text-tertiary)] shrink-0 ml-2">{m.last_message ? new Date(m.last_message.created_at).toLocaleDateString() : ""}</span>
                      </div>
                      {m.last_message && <p className={`text-xs truncate ${m.unread_count > 0 ? "text-[var(--text-primary)] font-medium" : "text-[var(--text-tertiary)]"}`}>{m.last_message.sender_id === user?.id ? "You: " : ""}{m.last_message.body}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {withoutMessages.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">New Matches — Say Hello!</div>
              {withoutMessages.map((m) => {
                const other = m.other_user || {};
                return (
                  <button key={m.id} onClick={() => setActiveMatch(m)} className="w-full card-surface p-3 flex items-center gap-3 text-left border-l-4 border-[var(--brand-accent)]">
                    <Avatar member={other} size={11} />
                    <div className="flex-1">
                      <div className="font-medium text-[var(--brand-primary)]">{other.name}</div>
                      <div className="text-xs text-[var(--brand-accent)]">New match — start the conversation!</div>
                    </div>
                    <ChevronRight size={14} className="text-[var(--text-tertiary)]" />
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Connect Profile Tab ───────────────────────────────────────────────────────
function MyConnectProfileTab({ profile, onUpdated }) {
  const [form, setForm] = useState({
    intent: profile.intent || "friendship",
    visible: profile.visible !== false,
    favorite_hymn: profile.favorite_hymn || "",
    choir_experience: profile.choir_experience || "",
    service_preference: profile.service_preference || "",
    spiritual_habits: profile.spiritual_habits || "",
    learn_hymns_together: profile.learn_hymns_together || false,
    study_constitution_together: profile.study_constitution_together || false,
    cpmwave_interest: profile.cpmwave_interest || false,
    icebreaker_1: profile.icebreaker_1 || "",
    icebreaker_2: profile.icebreaker_2 || "",
    instruments: profile.instruments || [],
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await http.put("/connect/me", form);
      toast.success("Profile updated");
      onUpdated();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-xl">
      <div className="card-surface p-5 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">My Intention</div>
        <div className="grid grid-cols-2 gap-2">
          {[["friendship", "Friendship", Users], ["relationship", "Relationship", Heart], ["marriage", "Marriage-Minded", HeartHandshake], ["paused", "Paused", VolumeX]].map(([v, l, Icon]) => (
            <button key={v} onClick={() => setForm((f) => ({ ...f, intent: v }))} className={`flex items-center gap-2 p-2.5 rounded-xl border text-sm transition-all ${form.intent === v ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white" : "border-[var(--border-default)] hover:border-[var(--brand-primary)]/40"}`}>
              <Icon size={14} /> {l}
              {form.intent === v && <CheckCircle2 size={12} className="ml-auto" />}
            </button>
          ))}
        </div>
      </div>

      <div className="card-surface p-5 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">Faith Details</div>
        {[["favorite_hymn", "Favourite CCC Hymn", "e.g. Hymn 1 — Holy Holy Holy"], ["choir_experience", "Choir / Music Experience", "e.g. Soprano in choir, 5 years"], ["service_preference", "Service Preference", "e.g. Intercessory prayer, Ushering"], ["spiritual_habits", "Spiritual Habits", "e.g. Daily morning prayer, Bible study"]].map(([key, label, ph]) => (
          <label key={key} className="text-sm block">
            {label}
            <input className="input-clean mt-1 w-full" value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} placeholder={ph} />
          </label>
        ))}
        <div className="space-y-2">
          {[["learn_hymns_together", "Open to learning hymns together"], ["study_constitution_together", "Open to Constitution study together"], ["cpmwave_interest", "Interested in sharing CPM Wave music"]].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))} className="accent-[var(--brand-primary)]" />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="card-surface p-5 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">Conversation Starters</div>
        {[["icebreaker_1", "First prompt"], ["icebreaker_2", "Second prompt"]].map(([key, label]) => (
          <label key={key} className="text-sm block">
            {label}
            <select className="input-clean mt-1 w-full" value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}>
              <option value="">— Choose a prompt —</option>
              {ICEBREAKERS.map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
          </label>
        ))}
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer card-surface px-4 py-3">
        <input type="checkbox" checked={form.visible} onChange={(e) => setForm((f) => ({ ...f, visible: e.target.checked }))} className="accent-[var(--brand-primary)]" />
        Show my profile in Discover
      </label>

      <div className="flex items-center gap-2 bg-[var(--bg-subtle)] rounded-xl px-4 py-3 text-xs text-[var(--text-secondary)]">
        <Lock size={12} className="shrink-0 text-[var(--brand-accent)]" />
        Your Connect profile is separate from your main profile. Only matched members can chat with you.
      </div>

      <button onClick={save} disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 py-3">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <BadgeCheck size={16} />} Save Profile
      </button>
    </div>
  );
}

// ── Safety Tab ────────────────────────────────────────────────────────────────
function SafetyTab() {
  const sections = [
    {
      title: "Financial Safety",
      icon: Shield,
      color: "red",
      tips: [
        "Never send money to anyone you have met on this platform.",
        "Do not share bank account details, sort codes, or card numbers.",
        "Be suspicious of requests for gift cards, crypto, or emergency loans.",
        "Report anyone who asks for money immediately — this is a major scam signal.",
      ],
    },
    {
      title: "Identity & Trust",
      icon: BadgeCheck,
      color: "blue",
      tips: [
        "Look for the verified badge on profiles — these have been reviewed by admin.",
        "Be cautious of profiles that seem too perfect or rushed.",
        "Ask about their parish, shepherd, and length of membership.",
        "Take your time — there is no rush in a God-ordered relationship.",
      ],
    },
    {
      title: "Communication Safety",
      icon: MessageSquare,
      color: "amber",
      tips: [
        "Keep conversations on this platform until trust is well established.",
        "Be cautious if someone pushes hard to move to WhatsApp, Telegram, or phone too quickly.",
        "Do not share your home address, workplace, or daily routine early on.",
        "Trust your instincts — if something does not feel right, it probably is not.",
      ],
    },
    {
      title: "Romance Scam Warning Signs",
      icon: AlertTriangle,
      color: "orange",
      tips: [
        "Claims to be a military officer, oil rig worker, or UN peacekeeper abroad.",
        "Expresses intense love or devotion very quickly ('love bombing').",
        "Has an emergency that conveniently requires money.",
        "Cannot meet in person or by video call for various reasons.",
        "Has very few photos or their story keeps changing.",
      ],
    },
    {
      title: "Meeting in Person",
      icon: Church,
      color: "green",
      tips: [
        "First meetings should always be in public, ideally church or community settings.",
        "Tell someone you trust where you are going and who you are meeting.",
        "Never go alone to a private location with someone you have just met.",
        "Involve your pastor or a church elder early in serious courtship.",
      ],
    },
  ];

  const colorMap = {
    red: "bg-red-50 border-red-200 text-red-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    green: "bg-emerald-50 border-emerald-200 text-emerald-700",
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="card-surface p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--brand-primary)] grid place-items-center">
            <Shield size={18} className="text-[var(--brand-accent)]" />
          </div>
          <div>
            <div className="font-display text-lg text-[var(--brand-primary)]">Your Safety Matters</div>
            <div className="text-xs text-[var(--text-secondary)]">Celestial Match is built with your protection in mind</div>
          </div>
        </div>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
          Celestial Match uses AI-assisted moderation, keyword filtering, and admin oversight to protect you. However, your vigilance is the most important layer of protection. Please read these guidelines carefully.
        </p>
      </div>

      {sections.map((sec) => {
        const Icon = sec.icon;
        const cls = colorMap[sec.color] || colorMap.blue;
        return (
          <div key={sec.title} className={`card-surface border rounded-xl p-5 ${cls}`}>
            <div className="flex items-center gap-2 mb-3 font-semibold text-sm">
              <Icon size={15} /> {sec.title}
            </div>
            <ul className="space-y-1.5">
              {sec.tips.map((tip, i) => (
                <li key={i} className="text-xs flex items-start gap-2">
                  <span className="mt-0.5 shrink-0">•</span> {tip}
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <div className="card-surface p-5 space-y-3">
        <div className="font-semibold text-sm text-[var(--brand-primary)]">How to Report</div>
        <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
          You can report any member using the Flag icon inside a chat, or on their profile card. Reports go directly to our moderation team who will review within 24 hours. Repeated violations lead to account suspension.
        </p>
        <div className="text-xs text-[var(--text-tertiary)] bg-[var(--bg-subtle)] rounded-lg px-3 py-2">
          All reports are confidential. The reported member will not know who submitted the report.
        </div>
      </div>
    </div>
  );
}

// ── Testimonies Section ───────────────────────────────────────────────────────
function TestimoniesSection() {
  const [testimonies, setTestimonies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    http.get("/connect/testimonies")
      .then((r) => setTestimonies(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (testimonies.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">Community Testimonies</div>
      {testimonies.slice(0, 3).map((t) => (
        <div key={t.id} className="card-surface p-4 border-l-4 border-[var(--brand-accent)]">
          <div className="flex items-center gap-2 mb-2">
            <Avatar member={t.user1} size={7} />
            <Avatar member={t.user2} size={7} />
            <span className="text-xs font-medium text-[var(--brand-primary)]">{t.user1?.name} & {t.user2?.name}</span>
            {t.relationship_status && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">{REL_STATUS_LABELS[t.relationship_status]}</span>}
          </div>
          <div className="font-semibold text-sm text-[var(--brand-primary)]">{t.testimony?.title}</div>
          <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-3">{t.testimony?.story}</p>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AlleluiaConnect() {
  const { user } = useAuth();
  const [tab, setTab] = useState("discover");
  const [eligibility, setEligibility] = useState(null);
  const [connectProfile, setConnectProfile] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeChatMatch, setActiveChatMatch] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [eligRes, profileRes, settingsRes] = await Promise.all([
        http.get("/connect/eligibility"),
        http.get("/connect/me"),
        http.get("/connect/settings"),
      ]);
      setEligibility(eligRes.data);
      setConnectProfile(profileRes.data && Object.keys(profileRes.data).length > 0 ? profileRes.data : null);
      setSettings(settingsRes.data);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-3">
        <Loader2 size={32} className="animate-spin text-[var(--brand-accent)]" />
        <p className="text-sm text-[var(--text-secondary)]">Loading Celestial Match…</p>
      </div>
    );
  }

  if (!settings?.enabled) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-[var(--bg-subtle)] grid place-items-center mx-auto">
          <Lock size={28} className="text-[var(--text-tertiary)]" />
        </div>
        <h2 className="font-display text-2xl text-[var(--brand-primary)]">Celestial Match</h2>
        <p className="text-sm text-[var(--text-secondary)]">This feature is not currently active. Please check back later or contact your church admin.</p>
      </div>
    );
  }

  if (eligibility && !eligibility.eligible) {
    return <EligibilityGate missing={eligibility.missing} />;
  }

  if (!connectProfile) {
    return <ConnectProfileSetup onSaved={loadAll} />;
  }

  const TABS = [
    { k: "discover", l: "Discover", icon: Compass },
    { k: "matches", l: "Matches", icon: Heart },
    { k: "chats", l: "Chats", icon: MessageSquare },
    { k: "profile", l: "My Profile", icon: User },
    { k: "safety", l: "Safety", icon: Shield },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[var(--brand-accent)]">Celestial Match</div>
        <h1 className="font-display text-3xl text-[var(--brand-primary)]">Faith-Centred Connections</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Find trusted friendships and serious relationships within the Celestial community.</p>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-hide">
        {TABS.map(({ k, l, icon: Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-2.5 min-h-[44px] rounded-xl text-sm whitespace-nowrap font-semibold transition-all shrink-0 active:scale-95 ${tab === k ? "bg-[var(--brand-primary)] text-white shadow-md" : "bg-[var(--bg-subtle)] text-[var(--text-secondary)] hover:bg-[var(--bg-default)]"}`}
          >
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "discover" && (
        <div className="space-y-4">
          <DiscoverTab settings={settings} />
          <TestimoniesSection />
        </div>
      )}
      {tab === "matches" && <MatchesTab onOpenChat={(m) => { setActiveChatMatch(m); setTab("chats"); }} />}
      {tab === "chats" && <ChatsTab initialMatch={activeChatMatch} />}
      {tab === "profile" && <MyConnectProfileTab profile={connectProfile} onUpdated={loadAll} />}
      {tab === "safety" && <SafetyTab />}
    </div>
  );
}
